'use client';

import { useState, useEffect } from 'react';
import Image from 'next/image';
import { useRouter } from 'next/navigation';
import axios from 'axios';
import toast from 'react-hot-toast';
import { useAuthStore } from '@/store/useAuthStore';
import {
  LuCalendar,
  LuCheck,
  LuChevronDown,
  LuChevronUp,
  LuMail,
  LuBadgeCheck,
  LuFileText,
  LuSearch,
  LuMegaphone,
  LuTicket,
  LuUserCheck,
  LuTag,
  LuMessageCircle,
  LuHeadphones,
  LuMapPin,
} from 'react-icons/lu';
import dynamic from 'next/dynamic';
const ArticleContent = dynamic(
  () => import('@/components/articles/ArticleContent').then((m) => m.ArticleContent),
  { ssr: false }
);

// ─── Types ────────────────────────────────────────────────────────────────────

interface ActiveCycle {
  id: string;
  title: string;
  slug: string;
  subTitle: string | null;
  shortDescription: string | null;
  description: string;
  eligibilityCriteria: string;
  fundingAmountBdt: number;
  maxApplicants: number | null;
  applicationOpenDate: string;
  applicationCloseDate: string;
  disbursementDate: string | null;
  heroImageUrl: string | null;
  ctaLabel: string | null;
  seoTitle: string | null;
  seoMetaDescription: string | null;
  _count: { applications: number };
}

interface GrantWinner {
  id: string;
  name: string;
  country: string;
  program: string;
  intake: string;
  quote: string;
  bio: string | null;
  cycleYear: number;
  cycleTitle: string | null;
  photoUrl: string | null;
}

// ─── Helpers ──────────────────────────────────────────────────────────────────

function fmtDate(d: string | null | undefined) {
  if (!d) return '—';
  return new Date(d).toLocaleDateString('en-GB', { day: 'numeric', month: 'long', year: 'numeric' });
}

function isApplicationOpen(cycle: ActiveCycle): boolean {
  const now = new Date();
  return now >= new Date(cycle.applicationOpenDate) && now <= new Date(cycle.applicationCloseDate);
}

function isBeforeOpen(cycle: ActiveCycle): boolean {
  return new Date() < new Date(cycle.applicationOpenDate);
}

function isBeforeResults(cycle: ActiveCycle): boolean {
  if (!cycle.disbursementDate) return false;
  return new Date() <= new Date(cycle.disbursementDate);
}

// ─── Static content ───────────────────────────────────────────────────────────

const STATIC_ELIGIBILITY = [
  'Has to be a Bangladeshi national',
  'Applied to, admitted by, or expecting an offer from an international university',
  'Planning to travel within the next 12 months',
];

const steps = [
  {
    num: '1',
    icon: LuFileText,
    title: 'Apply (20–25 min)',
    body: 'Fill out the form. Answer three questions. Attach your CV, transcript, and offer letter if you have it.',
  },
  {
    num: '2',
    icon: LuSearch,
    title: 'Review',
    body: 'Every application is read by our team. We evaluate two tracks: commitment and readiness, and financial need. Each year, one winner is selected per track.',
  },
  {
    num: '3',
    icon: LuBadgeCheck,
    title: 'Shortlist + Selection',
    body: 'We will shortlist 10–15 applications and conduct a short phone interview, and select the winners.',
  },
  {
    num: '4',
    icon: LuMegaphone,
    title: 'Announcement',
    body: 'Winners announced July 25, privately contacted before the public post.',
  },
  {
    num: '5',
    icon: LuTicket,
    title: 'Book via GoZayaan',
    body: 'Visit a GoZayaan Experience Center in Banani or Chattogram. Phone support available for outstation winners.',
  },
];

const faqs: { q: string; a: string }[] = [
  {
    q: 'Do I need a Lattu.ai account to apply?',
    a: "Yes. Your application is linked to a Lattu.ai account. Creating one is free and takes under a minute. You'll be prompted to sign up when you click Apply.",
  },
  {
    q: 'Do I need an offer letter to apply?',
    a: "You don't need an offer letter to submit your application. But, you'll need to show us your offer/acceptance/admission letter if you make it to the shortlist. If you're expecting yours within the application window, apply now.",
  },
  {
    q: 'Can I apply if my offer is conditional?',
    a: 'Yes, conditional offers can be accepted, but final grant release may require additional verification.',
  },
  {
    q: 'How much will I actually receive?',
    a: 'Your grant is determined by your destination. The maximum one can be rewarded is BDT 150,000. Our team will provide the details if you are shortlisted.',
  },
  {
    q: 'Will I receive the money directly?',
    a: 'No. The grant is provided as reimbursement. Selected students will need to submit their flight booking confirmation/e-ticket and payment receipt. Once verified, Lattu AI will reimburse the approved airfare amount.',
  },
  {
    q: 'How long is the grant valid?',
    a: '12 months from the date of announcement. You must travel within this window. A one-time extension of 6 months is available on request if you face delays (visa, deferred intake, etc.).',
  },
  {
    q: 'When will I hear back?',
    a: 'Results are announced on July 25. Shortlisted applicants will be contacted via email/phone.',
  },
  {
    q: 'Will all applicants receive feedback?',
    a: 'No, due to the number of applications, individual feedback may not be possible.',
  },
  {
    q: 'Is there a repayment requirement?',
    a: 'No. This is a non-repayable grant. We only ask that you travel within the 12-month validity period.',
  },
  {
    q: "Can I apply again next year if I'm not selected?",
    a: 'Yes. There is no limit on how many times you can apply across cycles. Previous winners are not eligible to reapply.',
  },
  {
    q: "What is GoZayaan's role in the grant?",
    a: 'GoZayaan is the Official Booking Partner for the Lattu Launchpad Grant 2026. Selected winners will receive guided flight booking support through GoZayaan after final selection.',
  },
  {
    q: 'Will GoZayaan fund the grant?',
    a: 'No. Lattu AI funds the grant. GoZayaan supports the selected winners with flight booking guidance, student fare consultation, eligible airline offers where available, and post-booking support.',
  },
  {
    q: 'How will winners book their flights?',
    a: "Selected winners will book through GoZayaan's offline booking process. Winners can visit the Banani or Chattogram GoZayaan Experience Center. Students outside Dhaka or Chattogram can receive phone-based support after verification.",
  },
  {
    q: 'What extra support will winners receive?',
    a: 'Selected winners will receive free consultation from GoZayaan travel experts, guided booking support, access to eligible student airline offers where available, post-booking support, and an individual point of contact.',
  },
];

// ─── Sub-components ───────────────────────────────────────────────────────────

function EmailNotifyForm({ source }: { source: string }) {
  const [email, setEmail] = useState('');
  const [submitted, setSubmitted] = useState(false);
  const [loading, setLoading] = useState(false);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!email.trim()) return;
    setLoading(true);
    try {
      await axios.post(`${process.env.NEXT_PUBLIC_API_BASE_URL}/launchpad/notify`, {
        email: email.trim(),
        source,
      });
      setSubmitted(true);
      toast.success("You're on the list. We'll email you when the next cycle opens.");
    } catch {
      toast.error('Something went wrong. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  if (submitted) {
    return (
      <div className="flex items-center gap-2 text-sm font-medium text-[#615fff]">
        <LuCheck size={16} />
        Got it — we will email you when the next cycle opens.
      </div>
    );
  }

  return (
    <form onSubmit={handleSubmit} className="flex flex-col sm:flex-row gap-3 max-w-lg w-full">
      <label htmlFor={`notify-${source}`} className="sr-only">Email address</label>
      <input
        id={`notify-${source}`}
        type="email"
        required
        value={email}
        onChange={(e) => setEmail(e.target.value)}
        placeholder="your@email.com"
        className="flex-1 rounded-xl border border-gray-200 dark:border-white/10 bg-white dark:bg-white/5 px-5 py-4 text-base text-black dark:text-white placeholder:text-zinc-400 outline-none focus:border-[#615fff] transition-colors"
      />
      <button
        type="submit"
        disabled={loading}
        className="bg-[#615fff] hover:bg-[#4f4ecc] text-white font-medium px-7 py-4 rounded-xl transition-colors disabled:opacity-60 shrink-0"
      >
        {loading ? 'Saving…' : 'Notify'}
      </button>
    </form>
  );
}

function FaqItem({ q, a }: { q: string; a: string }) {
  const [open, setOpen] = useState(false);
  return (
    <div className="rounded-2xl bg-white dark:bg-white/5 border border-gray-200 dark:border-white/10 overflow-hidden">
      <button
        type="button"
        onClick={() => setOpen(!open)}
        className="w-full flex items-center justify-between gap-4 px-6 py-5 text-left focus:outline-none focus-visible:ring-2 focus-visible:ring-[#615fff]"
        aria-expanded={open}
      >
        <span className="font-semibold text-sm text-black dark:text-white">{q}</span>
        {open
          ? <LuChevronUp size={18} className="shrink-0 text-[#615fff]" />
          : <LuChevronDown size={18} className="shrink-0 text-zinc-400" />}
      </button>
      {open && (
        <div className="px-6 pb-5">
          <p className="text-sm leading-relaxed text-zinc-600 dark:text-zinc-400">{a}</p>
        </div>
      )}
    </div>
  );
}

// ─── Page ─────────────────────────────────────────────────────────────────────

const APPLY_URL = '/launchpad-grant/apply/';

export default function LaunchpadGrantPage() {
  const router = useRouter();
  const { user } = useAuthStore();
  const [cycle, setCycle] = useState<ActiveCycle | null>(null);
  const [winners, setWinners] = useState<GrantWinner[]>([]);
  const [loading, setLoading] = useState(true);

  const handleApply = () => {
    if (user) {
      router.push(APPLY_URL);
    } else {
      router.push(`/login?redirect=${encodeURIComponent(APPLY_URL)}`);
    }
  };

  useEffect(() => {
    Promise.all([
      axios
        .get<ActiveCycle[]>(`${process.env.NEXT_PUBLIC_API_BASE_URL}/launchpad/cycles`)
        .then((r) => r.data[0] ?? null)
        .catch(() => null),
      axios
        .get<GrantWinner[]>(`${process.env.NEXT_PUBLIC_API_BASE_URL}/launchpad/winners`)
        .then((r) => r.data ?? [])
        .catch(() => []),
    ]).then(([activeCycle, pastWinners]) => {
      setCycle(activeCycle);
      setWinners(pastWinners);
      setLoading(false);
    });
  }, []);

  const open = cycle ? isApplicationOpen(cycle) : false;
  const notYetOpen = cycle ? isBeforeOpen(cycle) : false;
  const beforeResults = cycle ? isBeforeResults(cycle) : false;
  // Cycle is "closed" once it's neither open nor upcoming — covers a past/finished
  // cycle as well as no active cycle at all.
  const cycleClosed = !loading && !open && !notYetOpen;

  const keyDates = cycle
    ? [
        { icon: '📅', label: 'Applications open', date: fmtDate(cycle.applicationOpenDate) },
        { icon: '📅', label: 'Applications close', date: fmtDate(cycle.applicationCloseDate) },
        ...(cycle.disbursementDate
          ? [{ icon: '📅', label: 'Results announced', date: fmtDate(cycle.disbursementDate) }]
          : []),
      ]
    : [];

  const winnersByYear = winners.reduce<Record<number, GrantWinner[]>>((acc, w) => {
    if (!acc[w.cycleYear]) acc[w.cycleYear] = [];
    acc[w.cycleYear].push(w);
    return acc;
  }, {});
  const winnerYears = Object.keys(winnersByYear).map(Number).sort((a, b) => b - a);
  const hasWinners = winnerYears.length > 0;

  // Alternate section backgrounds dynamically so two consecutive *visible*
  // sections never share the same color, regardless of which ones are hidden.
  const WHITE_BG = 'bg-white dark:bg-[#111827]';
  const GRAY_BG = 'bg-[#ECEFF1] dark:bg-[#1a1a1a]';
  const sectionVisibility = {
    pastWinners: !loading && hasWinners,
    eligibility: !cycleClosed,
    howWeSelect: true,
    keyDates: !loading && !!cycle && keyDates.length > 0,
    goZayaan: !cycleClosed,
    faq: !cycleClosed,
    footerCta: true,
  };
  const sectionBg: Record<keyof typeof sectionVisibility, string> = {} as Record<keyof typeof sectionVisibility, string>;
  let nextIsWhite = true;
  (Object.keys(sectionVisibility) as (keyof typeof sectionVisibility)[]).forEach((key) => {
    if (!sectionVisibility[key]) return;
    sectionBg[key] = nextIsWhite ? WHITE_BG : GRAY_BG;
    nextIsWhite = !nextIsWhite;
  });

  return (
    <div className="w-full bg-transparent">

      {/* ── HERO ─────────────────────────────────────────────────────────────── */}
      <section className="relative overflow-hidden pt-20 pb-16 lg:pt-28 lg:pb-20 bg-transparent">
        {cycle?.heroImageUrl ? (
          <div className="absolute inset-0 -z-10">
            <Image src={cycle.heroImageUrl} alt="" fill className="object-cover opacity-15 dark:opacity-10" priority />
            <div className="absolute inset-0 bg-linear-to-b from-white/60 via-transparent to-white dark:from-[#0f1623]/70 dark:via-transparent dark:to-[#0f1623]" />
          </div>
        ) : (
          <div className="pointer-events-none absolute top-0 left-1/2 -translate-x-1/2 w-200 h-100 bg-indigo-500/10 rounded-full blur-[120px]" />
        )}

        <div className="relative max-w-5xl mx-auto px-6">
          <div className="flex items-center gap-3 mb-5">
            <span className="text-xs font-bold uppercase tracking-[0.25em] text-indigo-500">Lattu AI</span>
            <span className="text-xs font-semibold px-2.5 py-1 rounded-full bg-indigo-500/10 text-indigo-500 border border-indigo-500/25">
              Grant Program
            </span>
          </div>

          <h1 className="text-4xl lg:text-6xl font-black tracking-tight leading-tight mb-4 text-black dark:text-white">
            {cycle?.title ?? 'Lattu Launchpad Grant'}
          </h1>
          <p className="text-xl lg:text-2xl font-semibold mb-5 text-zinc-800 dark:text-zinc-400">
            {cycle?.subTitle ?? 'Funding your first step towards study abroad'}
          </p>
          {cycle?.shortDescription ? (
            <div
              className="text-base lg:text-lg leading-relaxed mb-10 text-zinc-800 dark:text-zinc-400 [&>p]:m-0 [&_img[data-inline-logo]]:inline [&_img[data-inline-logo]]:align-middle [&_img[data-inline-logo]]:h-[2.4em]! [&_img[data-inline-logo]]:object-contain"
              dangerouslySetInnerHTML={{ __html: cycle.shortDescription }}
            />
          ) : (
            <p className="text-base lg:text-lg leading-relaxed mb-10 text-zinc-800 dark:text-zinc-400">
              {"Every year, we award Bangladeshi students a grant toward their study abroad journey. You've done a lot of work to get here. Lattu Launchpad Grant exists to support students who have already taken a major step."}
            </p>
          )}

          {/* Info boxes */}
          {/* {!loading && (
            cycle ? (
              <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-5 gap-3 mb-10">
                {[
                  { icon: LuAward, label: 'Per Recipient (BDT)', value: `Up to ${cycle.fundingAmountBdt.toLocaleString()}` },
                  { icon: LuUsers, label: 'Recipients', value: cycle.maxApplicants != null ? String(cycle.maxApplicants) : '—' },
                  { icon: LuCalendar, label: 'Opens', value: fmtDate(cycle.applicationOpenDate) },
                  { icon: LuCalendar, label: 'Closes', value: fmtDate(cycle.applicationCloseDate) },
                  ...(cycle.disbursementDate ? [{ icon: LuMegaphone, label: 'Results', value: fmtDate(cycle.disbursementDate) }] : []),
                ].map((item) => (
                  <div key={item.label} className="flex items-center gap-3 rounded-2xl bg-white dark:bg-white/5 border border-gray-200 dark:border-white/10 px-4 py-3">
                    <div className="w-8 h-8 rounded-lg flex items-center justify-center bg-[#615fff]/10 shrink-0">
                      <item.icon size={16} className="text-[#615fff]" />
                    </div>
                    <div>
                      <p className="text-xs font-semibold uppercase tracking-wider text-zinc-400 leading-none mb-0.5">{item.label}</p>
                      <p className="text-sm font-bold text-black dark:text-white">{item.value}</p>
                    </div>
                  </div>
                ))}
              </div>
            ) : (
              <div className="flex flex-wrap gap-3 mb-10">
                {[
                  { icon: LuUsers, label: '2 students will be awarded' },
                  { icon: LuAward, label: 'Up to BDT 150,000 each' },
                  { icon: LuClock, label: 'Valid for 12 months' },
                ].map((card) => (
                  <div key={card.label} className="flex items-center gap-3 rounded-2xl bg-white dark:bg-white/5 border border-gray-200 dark:border-white/10 px-4 py-3">
                    <div className="w-8 h-8 rounded-lg flex items-center justify-center bg-[#615fff]/10 shrink-0">
                      <card.icon size={16} className="text-[#615fff]" />
                    </div>
                    <span className="text-sm font-medium text-black dark:text-white">{card.label}</span>
                  </div>
                ))}
              </div>
            )
          )}
          {loading && (
            <div className="flex gap-3 mb-10">
              {[1, 2, 3].map((i) => <div key={i} className="h-12 w-44 rounded-2xl bg-gray-100 dark:bg-white/5 animate-pulse" />)}
            </div>
          )} */}

          {/* CTA */}
          {loading ? (
            <div className="h-12 w-36 rounded-xl bg-gray-100 dark:bg-white/5 animate-pulse" />
          ) : open ? (
            <button
              type="button"
              onClick={handleApply}
              className="inline-flex items-center gap-2 bg-[#615fff] hover:bg-[#4f4ecc] text-white font-medium px-6 py-3 rounded-xl transition-colors"
            >
              {cycle?.ctaLabel ?? 'Apply Now'} <span aria-hidden>→</span>
            </button>
          ) : notYetOpen && cycle ? (
            <div className="space-y-2">
              <p className="text-sm font-medium text-zinc-500 dark:text-zinc-400">
                Applications open {fmtDate(cycle.applicationOpenDate)} · Get notified →
              </p>
              <EmailNotifyForm source="hero" />
            </div>
          ) : (
            <div className="space-y-4">
              <p className="text-sm font-medium text-zinc-500 dark:text-zinc-400">
                {cycle && beforeResults && cycle.disbursementDate
                  ? `Applications closed · Results on ${fmtDate(cycle.disbursementDate)} · Get notified when the next cycle opens →`
                  : 'Applications closed. Get notified when the next cycle opens →'}
              </p>
              <EmailNotifyForm source="hero" />
            </div>
          )}
        </div>
      </section>

      {/* ── PAST WINNERS — right after hero, hidden when no winners ──────────── */}
      {!loading && hasWinners && (
        <section className={`py-20 lg:py-24 ${sectionBg.pastWinners}`}>
          <div className="max-w-5xl mx-auto px-6">
            <span className="text-xs font-bold uppercase tracking-[0.25em] text-indigo-500 block mb-3">Grant Recipients</span>
            <h2 className="text-3xl lg:text-4xl font-semibold tracking-tight text-black dark:text-white mb-10">
              Lattu AI Launchpad Grant Winners
            </h2>
            {winnerYears.map((year) => (
              <div key={year} className="mb-10 last:mb-0">
                <p className="text-xs font-bold uppercase tracking-widest text-zinc-400 mb-5">{year}</p>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-6">
                  {winnersByYear[year].map((w) => (
                    <div
                      key={w.id}
                      className="rounded-2xl bg-white dark:bg-white/5 border border-gray-200 dark:border-white/10 overflow-hidden"
                    >
                      {w.photoUrl && (
                        // eslint-disable-next-line @next/next/no-img-element
                        <img
                          src={w.photoUrl}
                          alt={w.name}
                          className="w-full h-64 object-cover"
                        />
                      )}
                      <div className="px-6 py-5">
                        <p className="font-semibold text-black dark:text-white">{w.name}</p>
                        <p className="text-xs text-zinc-500 dark:text-zinc-400 mt-0.5">
                          {w.country} · {w.program} · {w.intake}
                        </p>
                        <p className="text-sm leading-relaxed text-zinc-600 dark:text-zinc-400 mt-3 italic">
                          &ldquo;{w.quote}&rdquo;
                        </p>
                        {w.bio && (
                          <div
                            className="mt-3 prose prose-sm dark:prose-invert max-w-none text-zinc-600 dark:text-zinc-400"
                            dangerouslySetInnerHTML={{ __html: w.bio }}
                          />
                        )}
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            ))}
          </div>
        </section>
      )}

      {/* ── DESCRIPTION — only when active cycle has description ─────────────── */}
      {/* {!loading && cycle?.description && cycle.description !== '<p></p>' && (
        <section className="py-20 lg:py-24 bg-transparent">
          <div className="max-w-5xl mx-auto px-6">
            <ArticleContent content={cycle.description} />
          </div>
        </section>
      )} */}

      {/* ── ELIGIBILITY — hidden once the cycle is closed ────────────────────── */}
      {!cycleClosed && (
      <section className={`py-20 lg:py-24 ${sectionBg.eligibility}`}>
        <div className="max-w-5xl mx-auto px-6">
          <span className="text-xs font-bold uppercase tracking-[0.25em] text-indigo-500 block mb-3">Eligibility</span>
          <h2 className="text-3xl lg:text-4xl font-semibold tracking-tight text-black dark:text-white mb-4">
            Who can apply
          </h2>
          <p className="text-base lg:text-lg max-w-2xl leading-relaxed text-zinc-600 dark:text-zinc-400 mb-8">
            Any Bangladeshi citizen planning to study abroad.
          </p>

          {cycle?.eligibilityCriteria && cycle.eligibilityCriteria !== '<p></p>' ? (
            <div className="mb-8">
              <ArticleContent content={cycle.eligibilityCriteria} />
            </div>
          ) : (
            <ul className="space-y-3 mb-8">
              {STATIC_ELIGIBILITY.map((item) => (
                <li key={item} className="flex items-start gap-3">
                  <LuCheck size={18} className="text-emerald-600 dark:text-emerald-400 shrink-0 mt-0.5" />
                  <span className="text-base text-black dark:text-white leading-relaxed">{item}</span>
                </li>
              ))}
            </ul>
          )}

          <div className="rounded-2xl border border-[#615fff]/25 bg-[#615fff]/5 px-6 py-5">
            <p className="text-sm leading-relaxed text-zinc-600 dark:text-zinc-400">
              <span className="font-semibold not-italic text-black dark:text-white">Note: </span>
              You&apos;ll need a valid offer or admission letter at the time of shortlisting, not at the time of applying.
              If you&apos;re still waiting on yours, go ahead and apply. You&apos;ll be asked to submit it if you make the shortlist.
            </p>
          </div>
        </div>
      </section>
      )}

      {/* ── HOW WE SELECT ─────────────────────────────────────────────────────── */}
      <section className={`py-20 lg:py-24 ${sectionBg.howWeSelect}`}>
        <div className="max-w-5xl mx-auto px-6">
          <span className="text-xs font-bold uppercase tracking-[0.25em] text-indigo-500 block mb-3">Selection Process</span>
          <h2 className="text-3xl lg:text-4xl font-semibold tracking-tight text-black dark:text-white mb-12">
            How we select
          </h2>
          <div className="relative">
            <div className="hidden lg:block absolute top-8 left-8 right-8 h-0.5 bg-linear-to-r from-[#615fff]/20 via-[#615fff]/40 to-[#615fff]/20" />
            <div className="grid grid-cols-1 lg:grid-cols-5 gap-8">
              {steps.map((step) => (
                <div key={step.num} className="relative flex flex-col items-start lg:items-center lg:text-center">
                  <div className="relative z-10 w-16 h-16 rounded-2xl flex items-center justify-center bg-[#615fff] text-white font-black text-xl mb-5 shadow-lg shadow-[#615fff]/20 shrink-0">
                    {step.num}
                  </div>
                  <h3 className="font-semibold text-base text-black dark:text-white mb-2">{step.title}</h3>
                  <p className="text-sm leading-relaxed text-zinc-600 dark:text-zinc-400">{step.body}</p>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* ── KEY DATES — only when active cycle ───────────────────────────────── */}
      {!loading && cycle && keyDates.length > 0 && (
        <section className={`py-20 lg:py-24 ${sectionBg.keyDates}`}>
          <div className="max-w-5xl mx-auto px-6">
            <span className="text-xs font-bold uppercase tracking-[0.25em] text-indigo-500 block mb-3">Timeline</span>
            <h2 className="text-3xl lg:text-4xl font-semibold tracking-tight text-black dark:text-white mb-8">
              Key dates
            </h2>
            <div className="grid grid-cols-1 sm:grid-cols-3 gap-5">
              {keyDates.map((d) => (
                <div
                  key={d.label}
                  className="rounded-2xl bg-white dark:bg-white/5 border border-gray-200 dark:border-white/10 px-6 py-6 flex items-start gap-4"
                >
                  <div className="w-10 h-10 rounded-xl flex items-center justify-center bg-[#615fff]/10 shrink-0">
                    <LuCalendar size={20} className="text-[#615fff]" />
                  </div>
                  <div>
                    <p className="text-sm font-semibold text-black dark:text-white">{d.label}</p>
                    <p className="text-sm text-[#615fff] font-medium mt-0.5">{d.date}</p>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </section>
      )}

      {/* ── GOZAYAAN PARTNERSHIP — hidden once the cycle is closed ────────────── */}
      {!cycleClosed && (
      <section className={`py-20 lg:py-24 ${sectionBg.goZayaan}`}>
        <div className="max-w-5xl mx-auto px-6">
          {/* Full-width header */}
          <span className="text-xs font-bold uppercase tracking-[0.25em] text-indigo-500 block mb-3">
            Official Booking Partner of the Lattu Launchpad Grant
          </span>
          <h2 className="text-3xl lg:text-4xl font-semibold tracking-tight text-black dark:text-white mb-10">
            Your flight booking,<br />in expert hands.
          </h2>

          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 lg:gap-16 items-start">

            {/* Left column */}
            <div>
              <p className="text-sm leading-relaxed text-zinc-600 dark:text-zinc-400 mb-4">
                When you win the Lattu Launchpad Grant, GoZayaan takes care of your flight booking. Each winner gets a dedicated travel specialist, exclusive airline access, and full support from route selection to departure.
              </p>
              <p className="text-sm leading-relaxed text-zinc-600 dark:text-zinc-400 mb-8">
                GoZayaan has been Bangladesh&apos;s trusted travel platform for years. Partnering with them means your booking is in capable hands.
              </p>

              {/* Logo + partner badge */}
              <div className="flex flex-wrap items-center gap-3 mb-6 p-4 rounded-lg border border-zinc-200 dark:border-white/10 bg-white dark:bg-transparent w-fit">
                <span className="text-xs text-zinc-500 whitespace-nowrap">Book flights via</span>
                <Image src="/go_zayaan_logo.png" alt="GoZayaan" width={108} height={34} className="object-contain dark:hidden" unoptimized />
                <Image src="/White.png" alt="GoZayaan" width={108} height={34} className="object-contain hidden dark:block" unoptimized />
                <span className="text-xs text-zinc-500 dark:text-zinc-400">Official Booking Partner of the Lattu Launchpad Grant</span>
              </div>

              {/* Offline booking callout */}
              <div className="rounded-xl border border-indigo-100 dark:border-indigo-900/40 bg-indigo-50/60 dark:bg-indigo-950/30 px-5 py-4 flex gap-3 items-start">
                <LuMapPin size={16} className="text-indigo-400 shrink-0 mt-0.5" />
                <p className="text-xs leading-relaxed text-zinc-600 dark:text-zinc-400">
                  Bookings are made in person at a GoZayaan Experience Center in Banani, Dhaka or Chattogram. Winners outside these cities can book by phone with valid ID and their grant award documentation.
                </p>
              </div>
            </div>

            {/* Right column — feature list */}
            <div className="space-y-6">
              {[
                {
                  icon: LuUserCheck,
                  title: 'Dedicated booking specialist',
                  body: 'A named GoZayaan point of contact for your entire booking window.',
                },
                {
                  icon: LuTag,
                  title: 'Exclusive airline offers',
                  body: 'Special fare discounts and additional baggage allowances, where available.',
                },
                {
                  icon: LuMessageCircle,
                  title: 'Free travel consultation',
                  body: "GoZayaan's travel experts guide you on routes, fare classes, and timing.",
                },
                {
                  icon: LuHeadphones,
                  title: 'Post-booking support',
                  body: 'Help with changes or queries right up until your departure.',
                },
              ].map((item) => (
                <div key={item.title} className="flex gap-4 items-start">
                  <div className="w-10 h-10 rounded-xl flex items-center justify-center bg-[#615fff]/10 shrink-0">
                    <item.icon size={18} className="text-[#615fff]" />
                  </div>
                  <div>
                    <h3 className="font-semibold text-sm text-black dark:text-white mb-1">{item.title}</h3>
                    <p className="text-sm leading-relaxed text-zinc-600 dark:text-zinc-400">{item.body}</p>
                  </div>
                </div>
              ))}
            </div>

          </div>
        </div>
      </section>
      )}

      {/* ── FAQ — hidden once the cycle is closed ─────────────────────────────── */}
      {!cycleClosed && (
      <section className={`py-20 lg:py-24 ${sectionBg.faq}`}>
        <div className="max-w-5xl mx-auto px-6">
          <span className="text-xs font-bold uppercase tracking-[0.25em] text-indigo-500 block mb-3">FAQ</span>
          <h2 className="text-3xl lg:text-4xl font-semibold tracking-tight text-black dark:text-white mb-8">
            Frequently asked questions
          </h2>
          <div className="space-y-3">
            {faqs.map((faq) => (
              <FaqItem key={faq.q} q={faq.q} a={faq.a} />
            ))}
          </div>
        </div>
      </section>
      )}

      {/* ── FOOTER CTA ────────────────────────────────────────────────────────── */}
      <section className={`py-20 lg:py-24 ${sectionBg.footerCta}`}>
        <div className="max-w-3xl mx-auto px-6 text-center">
          <div className="w-14 h-14 rounded-2xl flex items-center justify-center bg-[#615fff]/10 mx-auto mb-6">
            <LuMail size={28} className="text-[#615fff]" />
          </div>
          <h2 className="text-3xl lg:text-4xl font-semibold tracking-tight text-black dark:text-white mb-4">
            Ready to apply?
          </h2>
          {!loading && open ? (
            <>
              <p className="text-base text-zinc-600 dark:text-zinc-400 mb-8 max-w-xl mx-auto">
                Applications take 20–25 minutes. You can save your progress and return anytime.
              </p>
              <button
                type="button"
                onClick={handleApply}
                className="inline-flex items-center gap-2 bg-[#615fff] hover:bg-[#4f4ecc] text-white font-medium px-6 py-3 rounded-xl transition-colors"
              >
                {cycle?.ctaLabel ?? 'Apply Now'} <span aria-hidden>→</span>
              </button>
            </>
          ) : !loading && notYetOpen && cycle ? (
            <>
              <p className="text-base text-zinc-600 dark:text-zinc-400 mb-8 max-w-xl mx-auto">
                Applications open {fmtDate(cycle.applicationOpenDate)} · Get notified →
              </p>
              <div className="flex justify-center">
                <EmailNotifyForm source="footer" />
              </div>
            </>
          ) : (
            <>
              <p className="text-base text-zinc-600 dark:text-zinc-400 mb-8 max-w-xl mx-auto">
                Get notified when the next cycle opens →
              </p>
              <div className="flex justify-center">
                <EmailNotifyForm source="footer" />
              </div>
            </>
          )}
        </div>
      </section>

    </div>
  );
}
